import { useSplitLayout } from '@components/app/split-layout/layout';
import { EntityIcon } from '@core/component/EntityIcon';
import { fileTypeToBlockName } from '@core/constant/allBlocks';
import WideChannel from '@icon/system/regular/hash-straight.svg';
import List from '@icon/system/regular/list.svg';
import type { NamedTool } from '@service-cognition/generated/tools/tool';
import { createMemo, createSignal } from 'solid-js';
import { VList } from 'virtua/solid';
import { BaseTool } from './BaseTool';
import { Tool } from './Tool';
import { createToolRenderer } from './ToolRenderer';

type ListEntitiesItem = NamedTool<
  'ListEntities',
  'response'
>['data']['items'][number];

const ListEntitiesToolResponse = (props: {
  items: ListEntitiesItem[];
  summary: string;
}) => {
  const results = createMemo(() => {
    const seen = new Set<string>();
    return props.items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  });

  const getItemTitle = (item: ListEntitiesItem): string => {
    switch (item.type) {
      case 'document':
        return item.name || 'Document';
      case 'aiChat':
      case 'agentSession':
        return item.name || 'Agent session';
      case 'project':
        return item.name || 'Project';
      case 'email':
        return item.subject || 'Email';
      case 'channel':
        return item.name || 'Channel';
      default:
        return 'Item';
    }
  };

  const getItemIcon = (item: ListEntitiesItem) => {
    switch (item.type) {
      case 'channel':
        return <WideChannel class="size-4" />;
      case 'document':
        return (
          <EntityIcon
            targetType={fileTypeToBlockName(
              item.subType ?? item.fileType,
              true
            )}
            size="xs"
            theme="monochrome"
          />
        );
      case 'aiChat':
      case 'agentSession':
        return <EntityIcon targetType="agent" size="xs" theme="monochrome" />;
      case 'project':
        return <EntityIcon targetType="project" size="xs" theme="monochrome" />;
      case 'email':
        return <EntityIcon targetType="email" size="xs" theme="monochrome" />;
      default:
        return undefined;
    }
  };

  const { openWithSplit } = useSplitLayout();

  const getClickHandler = (item: ListEntitiesItem) => {
    switch (item.type) {
      case 'document':
        return (event: MouseEvent) => {
          openWithSplit(
            {
              type: fileTypeToBlockName(item.subType ?? item.fileType),
              id: item.id,
            },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      case 'aiChat':
        return (event: MouseEvent) => {
          openWithSplit(
            { type: 'chat', id: item.id },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      case 'agentSession':
        return (event: MouseEvent) => {
          openWithSplit(
            { type: 'agent', id: item.id },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      case 'project':
        return (event: MouseEvent) => {
          openWithSplit(
            { type: 'project', id: item.id },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      case 'email':
        return (event: MouseEvent) => {
          openWithSplit(
            { type: 'email', id: item.id },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      case 'channel':
        return (event: MouseEvent) => {
          openWithSplit(
            { type: 'channel', id: item.id },
            { activate: true, preferNewSplit: event.shiftKey }
          );
        };
      default:
        return undefined;
    }
  };

  const itemHeight = 32;
  const maxHeight = 240;

  return (
    <Tool.List>
      <VList
        class="overscroll-contain"
        data={results()}
        bufferSize={itemHeight * 5}
        itemSize={itemHeight}
        style={{
          height: `${Math.min(results().length * itemHeight, maxHeight)}px`,
          contain: 'content',
        }}
      >
        {(item) => {
          const clickHandler = getClickHandler(item);

          return (
            <button
              type="button"
              class="block w-full text-left hover:bg-hover"
              onClick={clickHandler}
            >
              <Tool.ListItem icon={getItemIcon(item)}>
                <div class="truncate text-xs text-ink">
                  {getItemTitle(item)}
                </div>
              </Tool.ListItem>
            </button>
          );
        }}
      </VList>
    </Tool.List>
  );
};

const handler = createToolRenderer({
  name: 'ListEntities',
  render: (ctx) => {
    const [isExpanded, setIsExpanded] = createSignal(false);
    const items = () => ctx.response?.data.items ?? [];
    const dedupedCount = () => {
      const seen = new Set<string>();
      let count = 0;

      for (const item of items()) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        count += 1;
      }

      return count;
    };
    const hasResults = () => dedupedCount() > 0;
    const statusText = () => {
      if (!ctx.response) return undefined;
      if (dedupedCount() === 0) return 'No Results';
      if (dedupedCount() === 1) return '1 item';
      return `${dedupedCount()} items`;
    };

    return (
      <BaseTool
        icon={List}
        renderContext={ctx.renderContext}
        type="call"
        response={
          hasResults() && isExpanded() ? (
            <ListEntitiesToolResponse
              items={items()}
              summary={ctx.response?.data.summary ?? ''}
            />
          ) : undefined
        }
      >
        <div class="flex min-w-0 flex-1 items-center justify-between gap-3 overflow-hidden">
          <div class="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
            <span class="min-w-0 truncate">
              Filter for{' '}
              <span class="text-ink">
                {ctx.tool.data.includeTypes
                  ? ctx.tool.data.includeTypes.join(', ')
                  : 'All'}
              </span>{' '}
              ordered by{' '}
              <span class="text-ink">
                {ctx.tool.data.sortBy?.split('_').join(' ') ?? 'default'}
              </span>
            </span>
          </div>
          <Tool.ResultToggle
            expanded={isExpanded()}
            onToggle={() => setIsExpanded((expanded) => !expanded)}
            showToggle={hasResults()}
            status={statusText()}
          />
        </div>
      </BaseTool>
    );
  },
});

export const listEntitiesHandler = handler;
